import React, { useState, useMemo, useContext, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Stack,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { Search as SearchIcon } from "@mui/icons-material";
import { AUDIENCE_MAPPING_STATUSES } from "../../../constants";
import { DataTable } from "../../common/dataTable/DataTable";
import { AppAlert } from "../../common/appAlert/AppAlert";
import { mediaClassificationStrings } from "./locale";
import { initLocalization } from "../../../utils/helpers";
import { AppContext } from "../../../pages/root/Root";
import { useAudienceDomains } from "../../../hooks/audience/useAudienceDomains";
import { useMediaSearch } from "../../../hooks/audience/useMediaSearch";
import { useDomainActions } from "../../../hooks/audience/useDomainActions";
import { TABLE_ROWS_PER_PAGE } from "../../../constants";

export function MediaClassification() {
  initLocalization(mediaClassificationStrings);
  const { user } = useContext(AppContext);

  const [statusFilter, setStatusFilter] = useState("all");
  const [domainSearch, setDomainSearch] = useState("");

  const [mediaIdSearch, setMediaIdSearch] = useState("");
  const [mediaLabelSearch, setMediaLabelSearch] = useState("");

  const [selectedDomainId, setSelectedDomainId] = useState(null);
  const [selectedMediaId, setSelectedMediaId] = useState(null);

  const [domainPage, setDomainPage] = useState(0);
  const [domainRowsPerPage, setDomainRowsPerPage] = useState(
    TABLE_ROWS_PER_PAGE[0],
  );
  const [mediaPage, setMediaPage] = useState(0);
  const [mediaRowsPerPage, setMediaRowsPerPage] = useState(
    TABLE_ROWS_PER_PAGE[0],
  );

  const { domains, domainLoading, domainError, setDomainError, fetchDomains } =
    useAudienceDomains();
  const { mediaResults, mediaLoading, mediaError, setMediaError, searchMedia } =
    useMediaSearch();
  const {
    actionLoading,
    actionError,
    setActionError,
    mapDomain,
    rejectDomain,
  } = useDomainActions();

  useEffect(() => {
    fetchDomains(statusFilter, domainSearch);
    searchMedia(mediaIdSearch, mediaLabelSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (filter) => {
    setStatusFilter(filter);
    setSelectedDomainId(null);
    setActionError("");
    fetchDomains(filter, domainSearch);
  };

  const handleDomainSearch = () => {
    fetchDomains(statusFilter, domainSearch);
  };

  const handleMediaSearch = () => {
    searchMedia(mediaIdSearch, mediaLabelSearch);
  };

  const handleMap = async () => {
    if (!selectedDomainId || !selectedMediaId) return;
    const success = await mapDomain(
      selectedDomainId,
      selectedMediaId,
      user?.id,
    );
    if (success) {
      await fetchDomains(statusFilter, domainSearch);
      setSelectedMediaId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedDomainId) return;
    const success = await rejectDomain(selectedDomainId, user?.id);
    if (success) {
      await fetchDomains(statusFilter, domainSearch);
      setSelectedDomainId(null);
    }
  };

  const domainRows = useMemo(
    () =>
      domains.map((d) => ({
        id: d.audienceDomainId,
        Domain: d.audienceDomainName,
        Status: d.mappingStatusName || "Not mapped",
        Media: d.titleName || "—",
      })),
    [domains],
  );

  const mediaRows = useMemo(
    () =>
      mediaResults.map((m) => ({
        // id stays the real Guid: it's what gets sent to the map endpoint on selection.
        // ID (displayed) is the actual Source Id - previously this showed the Guid itself
        // under a "Source Id" header, which was just wrong.
        id: m.titleId,
        ID: m.sourceId,
        Media: m.titleName,
      })),
    [mediaResults],
  );

  // DataTable renders exactly the rows it's given (no built-in pagination/slicing) -
  // the pagination controls at the bottom are just a UI driven by page/rowsPerPage,
  // so the current page has to be sliced out here.
  const pagedDomainRows = useMemo(
    () =>
      domainRows.slice(
        domainPage * domainRowsPerPage,
        (domainPage + 1) * domainRowsPerPage,
      ),
    [domainRows, domainPage, domainRowsPerPage],
  );

  const pagedMediaRows = useMemo(
    () =>
      mediaRows.slice(
        mediaPage * mediaRowsPerPage,
        (mediaPage + 1) * mediaRowsPerPage,
      ),
    [mediaRows, mediaPage, mediaRowsPerPage],
  );

  useEffect(() => {
    setDomainPage(0);
  }, [domainRows]);

  useEffect(() => {
    setMediaPage(0);
  }, [mediaRows]);

  const domainCols = useMemo(
    () => [
      {
        field: "Domain",
        headerName: mediaClassificationStrings.domain,
        flex: 2,
        minWidth: 160,
      },
      {
        field: "Status",
        headerName: mediaClassificationStrings.status,
        minWidth: 120,
      },
      {
        field: "Media",
        headerName: mediaClassificationStrings.mappedMedia,
        flex: 2,
        minWidth: 140,
      },
    ],
    [mediaClassificationStrings.getLanguage()],
  );

  const mediaCols = useMemo(
    () => [
      { field: "ID", headerName: mediaClassificationStrings.id, minWidth: 70 },
      {
        field: "Media",
        headerName: mediaClassificationStrings.media,
        flex: 1,
        minWidth: 160,
      },
    ],
    [mediaClassificationStrings.getLanguage()],
  );

  const selectedDomain = domains.find(
    (d) => d.audienceDomainId === selectedDomainId,
  );

  return (
    <Box sx={{ height: "100%", p: 1 }}>
      <Grid container spacing={1.5} sx={{ height: "100%" }}>
        <Grid xs={12} md={6} sx={{ height: "100%" }}>
          <Paper
            sx={{
              p: 1.5,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {mediaClassificationStrings.leftPanelTitle}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1.5 }}
              alignItems="center"
            >
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="status-filter-label">
                  {mediaClassificationStrings.mappingStatus}
                </InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label={mediaClassificationStrings.mappingStatus}
                  onChange={(e) => handleFilterChange(e.target.value)}
                >
                  {AUDIENCE_MAPPING_STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                placeholder={mediaClassificationStrings.domainPlaceholder}
                value={domainSearch}
                onChange={(e) => setDomainSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleDomainSearch();
                }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleDomainSearch}
                sx={{ whiteSpace: "nowrap" }}
              >
                {mediaClassificationStrings.search}
              </Button>
            </Stack>

            <AppAlert
              open={!!domainError}
              type="error"
              onClose={() => setDomainError("")}
            >
              {domainError}
            </AppAlert>
            <AppAlert
              open={!!actionError}
              type="error"
              onClose={() => setActionError("")}
            >
              {actionError}
            </AppAlert>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
              }}
            >
              {domainLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <DataTable
                  rows={pagedDomainRows}
                  columns={domainCols}
                  selectedRows={selectedDomainId ? [selectedDomainId] : []}
                  onSelectRow={(_e, index) => {
                    const row = pagedDomainRows[index];
                    if (!row) return;
                    setSelectedDomainId(
                      row.id === selectedDomainId ? null : row.id,
                    );
                    setSelectedMediaId(null);
                  }}
                  allowSelection
                  showPagination
                  page={domainPage}
                  rowsPerPage={domainRowsPerPage}
                  totalRecords={domainRows.length}
                  onChangePage={(_e, newPage) => setDomainPage(newPage)}
                  onChangeRowsPerPage={(e) => {
                    setDomainRowsPerPage(parseInt(e.target.value, 10));
                    setDomainPage(0);
                  }}
                  tableMinWidth={400}
                  showNoDataMessage
                />
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                color="error"
                disabled={!selectedDomainId || actionLoading}
                onClick={handleReject}
              >
                {mediaClassificationStrings.reject}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                disabled={
                  !selectedDomainId || !selectedMediaId || actionLoading
                }
                onClick={handleMap}
              >
                {actionLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  mediaClassificationStrings.map
                )}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid xs={12} md={6} sx={{ height: "100%" }}>
          <Paper
            sx={{
              p: 1.5,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {mediaClassificationStrings.listingTitle}
            </Typography>

            {selectedDomain && (
              <Box
                sx={{
                  mb: 1.5,
                  p: 1,
                  backgroundColor: "#f6edfe",
                  borderRadius: 1,
                  borderLeft: "3px solid #6a4fa0",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {mediaClassificationStrings.selectedDomain}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedDomain.audienceDomainName}
                </Typography>
              </Box>
            )}

            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1.5 }}
              alignItems="center"
            >
              <TextField
                size="small"
                placeholder={mediaClassificationStrings.mediaIdPlaceholder}
                value={mediaIdSearch}
                onChange={(e) => setMediaIdSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleMediaSearch();
                }}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                placeholder={mediaClassificationStrings.mediaLabelPlaceholder}
                value={mediaLabelSearch}
                onChange={(e) => setMediaLabelSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleMediaSearch();
                }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleMediaSearch}
                sx={{ whiteSpace: "nowrap" }}
              >
                {mediaClassificationStrings.search}
              </Button>
            </Stack>

            <AppAlert
              open={!!mediaError}
              type="error"
              onClose={() => setMediaError("")}
            >
              {mediaError}
            </AppAlert>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
              }}
            >
              {mediaLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <DataTable
                  rows={pagedMediaRows}
                  columns={mediaCols}
                  selectedRows={selectedMediaId ? [selectedMediaId] : []}
                  onSelectRow={(_e, index) => {
                    const row = pagedMediaRows[index];
                    if (!row) return;
                    setSelectedMediaId(
                      row.id === selectedMediaId ? null : row.id,
                    );
                  }}
                  allowSelection
                  showPagination
                  page={mediaPage}
                  rowsPerPage={mediaRowsPerPage}
                  totalRecords={mediaRows.length}
                  onChangePage={(_e, newPage) => setMediaPage(newPage)}
                  onChangeRowsPerPage={(e) => {
                    setMediaRowsPerPage(parseInt(e.target.value, 10));
                    setMediaPage(0);
                  }}
                  tableMinWidth={280}
                  showNoDataMessage
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MediaClassification;
